import React, { useState, Fragment, useEffect, useMemo, useCallback } from 'react';

import { CustomDRTReportItem,  PDFImageProps, User } from '../../../utils/models';

import { getTutorials } from '../../../store/tutorials';
import { DEFAULT_REPORT_TITLE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';

import { getCoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';
import { getCurrentUser } from '../../../services/authService';
import { reArrangePhotoRecord, validateWorkOrderNumber } from '../../../utils/utilities';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import Button from '../../themed/button';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomCertificateForm, { ICertificate } from '../custom-general/custom-certificates/form';
import Conditional from '../../common/conditional';
import LoadingBadge from '../../themed/loading-badge';
import Drawing from '../../forms/custom-lpt/drawing';
import DRTReport from '.';
import DRTReportItems from './reportItems';
import Elevation from '../../themed/instruction-modals/elevation';
import ExecutiveSummaryForm from '../../forms/visual-inspection/executiveSummaryForm';
import FVICoverData from '../../themed/fvi-cover-data';
import Modal from '../../themed/modal';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import SyncTime from '../../blocks/sync-time';
import WarningNotice from '../custom-visual-inspection/warning-notice';
import VIPhotoRecordForm from '../../forms/visual-inspection/viPhotoRecordForm';
import Zippy from '../../themed/zippy';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CustomDRTReport: React.FC = () => {
	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const { role } = getCurrentUser() as User;

	const [previewReport, setPreviewReport] = useState(false);
	const [executiveSummary, setExecutiveSummary] = useState('');
	const [drtReport, setDRTReport] = useState<CustomDRTReportItem[]>([]);
	const [coverData, setCoverData] = useState(fviCoverData);
	const [photoRecord, setPhotoRecord] = useState<PDFImageProps[]>([]);
	const [drawing, setDrawing] = useState<PDFImageProps[]>([]);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [certificates, setCertificates] = useState<ICertificate>();

	const reportDetails = useMemo(() => {
		return {
			coverData,
			drawing,
			drtReport,
			executiveSummary,
			photoRecord,
			projectDetails,
			reportTitle,
			certificates
		};
	}, [coverData, certificates, drawing, drtReport, executiveSummary, photoRecord, projectDetails, reportTitle]);

	const validationMessage = useMemo(() => {
		if (!validateWorkOrderNumber(reportDetails.coverData.workOrderNumber)) {
            return 'Enter a valid work order number!';
        }

		const isMissingInspectionDate = reportDetails.drtReport.some((report) => !report.inspectionDate);
		if (isMissingInspectionDate) return 'Provide inspection dates for all items';
		
		const isMissingSerialNumber = reportDetails.drtReport.some((report) => !report.serialNumber);
		if (isMissingSerialNumber) return 'Provide serial numbers for all items';

		const isMissingWeldNumber = reportDetails.drtReport.some((report) => !report.weldNumber);
		if (isMissingWeldNumber) return 'Provide a valid weld number for all items!';

		const isMissingImage = reportDetails.drtReport.some((report) => !report.image || report.image.length === 0);
		if (isMissingImage) return 'Provide images for all items';

		if (reportDetails.photoRecord.length === 0) {
			return 'Please provide at least one photo';
		}

		if (reportDetails.drawing.length === 0) {
			return 'Please provide at least one drawing!';
		}

		if (reportDetails.executiveSummary.length === 0) {
			return 'Please provide an executive summary!';
		}

		return '';
	}, [reportDetails]);

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

	const handleSaveAndPreview = useCallback((): void => {
		autoSave.saveReport();
		togglePreviewReport();
	}, [autoSave, togglePreviewReport]);

	useEffect(() => {
		function initializeReport() {
			type ReportData = typeof reportDetails;
			
			const cachedReport = autoSave.oldReportData as ReportData | null;
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);
				
				return;
			}

			if (cachedReport.certificates) {
				setCertificates(cachedReport.certificates);
			}

			setExecutiveSummary(cachedReport.executiveSummary);
			setCoverData(cachedReport.coverData);
			setDrawing(cachedReport.drawing);
			setDRTReport(cachedReport.drtReport);
			setReportTitle(cachedReport.reportTitle);
			setPhotoRecord(reArrangePhotoRecord(cachedReport.photoRecord));
			dispatch(setLastPhotoNumber(cachedReport.photoRecord.length));
		} 

		initializeReport();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.DRT));
	}, [dispatch]);

	useEffect(() => {
		function updateReportItemsID() {
			const isAlreadyPopulated = reportDetails.drtReport.every((item) => item.id);
			if (isAlreadyPopulated) return;

			const updatedReportItems = reportDetails.drtReport.map((item, index) => ({ ...item, id: index + 1 }));
			setDRTReport(updatedReportItems);
		}

		function updateReportItemsShotNumber() {
			const isAlreadyPopulated = reportDetails.drtReport.every((item) => item.shotNumber);
			if (isAlreadyPopulated) return;

			const updatedReportItems = reportDetails.drtReport.map((item) => ({ 
				...item, 
				shotNumber: (item as any).drtShotNumber, 
				drtShotNumber: undefined 
			}));

			setDRTReport(updatedReportItems);
		}

		updateReportItemsID();
		updateReportItemsShotNumber();
	}, [reportDetails.drtReport]);

	useInspectedItemIDUpdate(drtReport.map((item) => item.shotNumber));

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<DRTReport {...reportDetails} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.exxonMobilDRT}
					isVisible={projectDetails?.inspectionTypeId === config.supportedTemplates.exxonMobilDRT && role.approveReports}
					subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisation.organisationName} ${projectDetails.inspectionTypeName} Reports`}
				/>
			)}

			<Conditional isVisible={validationMessage.length > 0}>
				<WarningNotice title='Incomplete Report' body={validationMessage} />
			</Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>

			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<ExecutiveSummaryForm initialSummary={executiveSummary} onProceed={setExecutiveSummary} />
			</Zippy>

			<CustomCertificateForm calibrationRequired certificates={certificates} onChangeCertificate={setCertificates} />

			<Zippy headerTitle="Drawing" isValid={drawing.length > 0}>
				<Drawing initialDrawing={drawing} onAdd={setDrawing} />
			</Zippy>

			<Zippy headerTitle="DRT Data Page" isValid={drtReport.length > 0}>
				<DRTReportItems reportItems={drtReport} onDataChange={setDRTReport} />
			</Zippy>

			<Zippy headerTitle="General Photos" isValid={photoRecord.length > 0}>
				<VIPhotoRecordForm initialGeneralPhotos={photoRecord} onProceed={setPhotoRecord} />
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving }
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export default CustomDRTReport;
